import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sparkles,
  Upload,
  Image as ImageIcon,
  Loader2,
  Trash2,
  Download,
  MoreHorizontal,
  X,
  Copy,
  RectangleHorizontal,
  Square,
  RectangleVertical,
  Maximize2,
  ChevronDown,
  Pencil,
  Heart,
  ArrowLeft,
  Check,
  CheckSquare,
  MessageSquare,
  XCircle,
} from "lucide-react";
import { useState, useRef, useCallback, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { Brand, Product, Creative, CreativeGroup } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { useRoute, useLocation } from "wouter";

const ASPECT_RATIOS = [
  { value: "1:1", label: "1:1", icon: Square },
  { value: "3:2", label: "3:2", icon: RectangleHorizontal },
  { value: "2:3", label: "2:3", icon: RectangleVertical },
  { value: "16:9", label: "16:9", icon: RectangleHorizontal },
  { value: "9:16", label: "9:16", icon: RectangleVertical },
];

const RESOLUTIONS = [
  { value: "2048", label: "2K" },
  { value: "4096", label: "4K" },
];

const QUANTITIES = [1, 2, 3, 4];

function getAspectClass(ratio: string) {
  switch (ratio) {
    case "1:1": return "aspect-square";
    case "3:2": return "aspect-[3/2]";
    case "2:3": return "aspect-[2/3]";
    case "16:9": return "aspect-video";
    case "9:16": return "aspect-[9/16]";
    default: return "aspect-square";
  }
}

async function downloadImage(url: string, filename: string) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, "_blank");
  }
}

export default function AdBuilder() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [, params] = useRoute("/create-ads/group/:id");
  const groupId = params?.id ? Number(params.id) : null;

  const [prompt, setPrompt] = useState("");
  const [imageModel, setImageModel] = useState("seedream-v5-lite");
  const [selectedBrandId, setSelectedBrandId] = useState<string>("");
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [resolution, setResolution] = useState("2048");
  const [quantity, setQuantity] = useState(1);
  const [cloneImageUrl, setCloneImageUrl] = useState<string>("");
  const [clonePreview, setClonePreview] = useState<string>("");
  const [multiCloneUrls, setMultiCloneUrls] = useState<{ imageUrl: string; preview: string }[]>([]);
  const [referenceImageUrls, setReferenceImageUrls] = useState<string[]>([]);
  const [referencePreviews, setReferencePreviews] = useState<string[]>([]);
  const [previewCreative, setPreviewCreative] = useState<Creative | null>(null);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [selectedForCopy, setSelectedForCopy] = useState<Set<number>>(new Set());

  const cloneInputRef = useRef<HTMLInputElement>(null);
  const refInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cloneParam = params.get("clone");
    const clonesParam = params.get("clones");

    if (clonesParam) {
      const loadMultiClones = async () => {
        try {
          const urls: string[] = JSON.parse(clonesParam);
          const loaded: { imageUrl: string; preview: string }[] = [];
          for (const url of urls) {
            try {
              const res = await fetch(url);
              if (!res.ok) continue;
              const blob = await res.blob();
              if (!blob.type.startsWith("image/")) continue;
              const formData = new FormData();
              formData.append("file", blob, "clone-template.jpg");
              const uploadRes = await fetch("/api/upload", { method: "POST", body: formData });
              const data = await uploadRes.json();
              loaded.push({ imageUrl: data.url, preview: url });
            } catch {
              continue;
            }
          }
          setMultiCloneUrls(loaded);
          if (loaded.length > 0) {
            setCloneImageUrl(loaded[0].imageUrl);
            setClonePreview(loaded[0].preview);
          }
          toast({
            title: `${loaded.length} template${loaded.length !== 1 ? "s" : ""} loaded`,
            description: `Ready to generate. Each template will create ${loaded.length > 1 ? "separate " : ""}ads.`,
          });
        } catch (err: any) {
          toast({ title: "Failed to load templates", description: err.message, variant: "destructive" });
        }
      };
      loadMultiClones();
      window.history.replaceState({}, "", window.location.pathname);
    } else if (cloneParam) {
      const uploadClone = async () => {
        try {
          const res = await fetch(cloneParam);
          if (!res.ok) throw new Error("Image not found");
          const blob = await res.blob();
          if (!blob.type.startsWith("image/")) throw new Error("Not a valid image");
          const formData = new FormData();
          formData.append("file", blob, "clone-template.jpg");
          const uploadRes = await fetch("/api/upload", { method: "POST", body: formData });
          const data = await uploadRes.json();
          setCloneImageUrl(data.url);
          setClonePreview(cloneParam);
          toast({ title: "Template loaded", description: "Clone image ready. Select a brand and product to generate." });
        } catch (err: any) {
          toast({ title: "Failed to load template", description: err.message, variant: "destructive" });
        }
      };
      uploadClone();
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const { data: group } = useQuery<CreativeGroup>({
    queryKey: ["/api/creative-groups", groupId],
    queryFn: async () => {
      const res = await fetch(`/api/creative-groups/${groupId}`);
      if (!res.ok) throw new Error("Group not found");
      return res.json();
    },
    enabled: !!groupId,
  });

  const { data: brands = [] } = useQuery<Brand[]>({ queryKey: ["/api/brands"] });

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ["/api/brands", selectedBrandId, "products"],
    queryFn: async () => {
      if (!selectedBrandId) return [];
      const res = await fetch(`/api/brands/${selectedBrandId}/products`);
      return res.json();
    },
    enabled: !!selectedBrandId,
  });

  const { data: creatives = [], isLoading } = useQuery<Creative[]>({
    queryKey: groupId ? [`/api/creative-groups/${groupId}/creatives`] : ["/api/creatives"],
    refetchInterval: (query) => {
      const data = query.state.data as Creative[] | undefined;
      if (data?.some((c) => c.status === "generating" || c.status === "pending")) return 3000;
      return false;
    },
  });

  const handleCloneUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setClonePreview(URL.createObjectURL(file));
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch("/api/upload", { method: "POST", body: formData });
    const data = await res.json();
    setCloneImageUrl(data.url);
    e.target.value = "";
  }, []);

  const handleReferenceUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (const file of Array.from(files)) {
      setReferencePreviews((prev) => [...prev, URL.createObjectURL(file)]);
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/upload", { method: "POST", body: formData });
      const data = await res.json();
      setReferenceImageUrls((prev) => [...prev, data.url]);
    }
    e.target.value = "";
  }, []);

  const generateMutation = useMutation({
    mutationFn: async () => {
      const cloneSources = multiCloneUrls.length > 1 ? multiCloneUrls : cloneImageUrl ? [{ imageUrl: cloneImageUrl, preview: clonePreview }] : [];

      if (cloneSources.length > 1) {
        const results: Creative[] = [];
        for (const clone of cloneSources) {
          const body: any = {
            prompt,
            brandId: selectedBrandId ? Number(selectedBrandId) : undefined,
            productId: selectedProductId ? Number(selectedProductId) : undefined,
            cloneImageUrl: clone.imageUrl,
            referenceImageUrls: referenceImageUrls.length > 0 ? referenceImageUrls : undefined,
            aspectRatio,
            resolution,
            quantity,
            groupId: groupId || undefined,
            imageModel,
          };
          const res = await apiRequest("POST", "/api/creatives/generate", body);
          const data = await res.json();
          results.push(...data);
        }
        return results;
      }

      const body: any = {
        prompt,
        brandId: selectedBrandId ? Number(selectedBrandId) : undefined,
        productId: selectedProductId ? Number(selectedProductId) : undefined,
        cloneImageUrl: cloneImageUrl || undefined,
        referenceImageUrls: referenceImageUrls.length > 0 ? referenceImageUrls : undefined,
        aspectRatio,
        resolution,
        quantity,
        groupId: groupId || undefined,
        imageModel,
      };
      const res = await apiRequest("POST", "/api/creatives/generate", body);
      return res.json();
    },
    onSuccess: (data: Creative[]) => {
      const key = groupId ? `/api/creative-groups/${groupId}/creatives` : "/api/creatives";
      queryClient.invalidateQueries({ queryKey: [key] });
      const totalAds = data.length;
      toast({
        title: "Generating...",
        description: `${totalAds} ad creative${totalAds !== 1 ? "s" : ""} are being generated. This may take a minute.`,
      });
    },
    onError: (err: any) => {
      toast({ title: "Generation failed", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/creatives/${id}`);
    },
    onSuccess: () => {
      const key = groupId ? `/api/creative-groups/${groupId}/creatives` : "/api/creatives";
      queryClient.invalidateQueries({ queryKey: [key] });
    },
  });

  const favoriteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("PATCH", `/api/creatives/${id}/favorite`);
      return res.json();
    },
    onSuccess: () => {
      const key = groupId ? `/api/creative-groups/${groupId}/creatives` : "/api/creatives";
      queryClient.invalidateQueries({ queryKey: [key] });
    },
  });

  const canGenerate = (
    (selectedBrandId && selectedProductId) ||
    cloneImageUrl ||
    referenceImageUrls.length > 0
  ) && !generateMutation.isPending;

  const completedCreatives = creatives.filter((c) => c.status === "completed" && !c.adStyle);
  const generatingCreatives = creatives.filter((c) => (c.status === "generating" || c.status === "pending") && !c.adStyle);
  const failedCreatives = creatives.filter((c) => c.status === "failed" && !c.adStyle);

  const displayCreatives = completedCreatives.filter((c) => {
    if (showFavoritesOnly && !c.isFavorite) return false;
    return true;
  });

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-2rem)] relative">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigate("/create-ads")}
              data-testid="button-back"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 data-testid="text-page-title" className="text-2xl font-bold text-gray-900 tracking-tight">
                {group?.name || "Create Ads"}
              </h1>
              <p className="text-gray-500 mt-1">
                Generate AI-powered ad creatives from your products and brand.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {completedCreatives.length > 0 && selectedForCopy.size > 0 && (
              <Button
                variant={selectedForCopy.size === displayCreatives.length ? "default" : "outline"}
                size="sm"
                className={cn("h-7 text-xs gap-1", selectedForCopy.size === displayCreatives.length && "bg-indigo-500 hover:bg-indigo-600")}
                onClick={() => {
                  if (selectedForCopy.size === displayCreatives.length) {
                    setSelectedForCopy(new Set());
                  } else {
                    setSelectedForCopy(new Set(displayCreatives.map(c => c.id)));
                  }
                }}
                data-testid="button-select-all-copy"
              >
                <CheckSquare className="h-3 w-3" />
                {selectedForCopy.size === displayCreatives.length ? "Deselect All" : "Select All"}
              </Button>
            )}
            {completedCreatives.length > 0 && (
              <Button
                variant={showFavoritesOnly ? "default" : "outline"}
                size="sm"
                className={cn("h-7 text-xs gap-1", showFavoritesOnly && "bg-rose-500 hover:bg-rose-600")}
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                data-testid="button-filter-favorites"
              >
                <Heart className={cn("h-3 w-3", showFavoritesOnly && "fill-current")} />
                Favorites
              </Button>
            )}
            {completedCreatives.length > 0 && (
              <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                {completedCreatives.length} generated
              </Badge>
            )}
            {generatingCreatives.length > 0 && (
              <Badge variant="secondary" className="bg-amber-50 text-amber-700 border-amber-200">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                {generatingCreatives.length} generating
              </Badge>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pb-48">
          {creatives.length === 0 && !isLoading ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="h-20 w-20 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                <Sparkles className="h-10 w-10 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No creatives yet</h3>
              <p className="text-sm text-gray-500 max-w-md">
                Select a brand and product below, then hit Generate to create your first AI-powered ad creative.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
              {generatingCreatives.map((creative) => (
                <div
                  key={creative.id}
                  data-testid={`card-creative-generating-${creative.id}`}
                  className="rounded-lg overflow-hidden"
                >
                  <div className={cn("bg-gradient-to-br from-primary/5 to-primary/10 flex items-center justify-center", getAspectClass(creative.aspectRatio || "1:1"))}>
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="h-8 w-8 text-primary animate-spin" />
                      <span className="text-sm text-primary font-medium">Generating...</span>
                    </div>
                  </div>
                </div>
              ))}

              {failedCreatives.map((creative) => (
                <div
                  key={creative.id}
                  data-testid={`card-creative-failed-${creative.id}`}
                  className="rounded-lg overflow-hidden relative group"
                >
                  <div className={cn("bg-red-50 flex items-center justify-center", getAspectClass(creative.aspectRatio || "1:1"))}>
                    <div className="flex flex-col items-center gap-2">
                      <X className="h-8 w-8 text-red-400" />
                      <span className="text-sm text-red-500 font-medium">Failed</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-1 right-1 h-6 w-6 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80"
                    onClick={() => deleteMutation.mutate(creative.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}

              {displayCreatives.map((creative) => {
                const isSelectedForCopy = selectedForCopy.has(creative.id);
                return (
                <div
                  key={creative.id}
                  data-testid={`card-creative-${creative.id}`}
                  className={cn(
                    "rounded-lg overflow-hidden group cursor-pointer relative",
                    isSelectedForCopy && "ring-2 ring-indigo-500 ring-offset-1"
                  )}
                  onClick={() => {
                    if (selectedForCopy.size > 0) {
                      setSelectedForCopy(prev => {
                        const next = new Set(prev);
                        if (next.has(creative.id)) next.delete(creative.id);
                        else next.add(creative.id);
                        return next;
                      });
                    } else {
                      navigate(`/creative/${creative.id}/edit`);
                    }
                  }}
                >
                  <img
                    src={creative.imageUrl || ""}
                    alt={creative.prompt || "Generated ad"}
                    className="w-full h-auto object-cover block"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />

                  <button
                    data-testid={`checkbox-select-copy-${creative.id}`}
                    className={cn(
                      "absolute top-2 left-2 h-6 w-6 rounded-md border-2 flex items-center justify-center transition-all z-10",
                      isSelectedForCopy
                        ? "bg-indigo-500 border-indigo-500 opacity-100"
                        : "bg-white/80 border-gray-300 opacity-0 group-hover:opacity-100"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedForCopy(prev => {
                        const next = new Set(prev);
                        if (next.has(creative.id)) next.delete(creative.id);
                        else next.add(creative.id);
                        return next;
                      });
                    }}
                  >
                    {isSelectedForCopy && <Check className="h-3.5 w-3.5 text-white" />}
                  </button>

                  <div className={cn(
                    "absolute top-2 right-2 flex items-center gap-1",
                    isSelectedForCopy ? "opacity-0" : "opacity-0 group-hover:opacity-100 transition-opacity"
                  )}>
                    <Button
                      data-testid={`button-favorite-${creative.id}`}
                      variant="secondary"
                      size="icon"
                      className={cn(
                        "h-7 w-7 shadow-sm",
                        creative.isFavorite
                          ? "bg-rose-500 hover:bg-rose-600 text-white"
                          : "bg-white/90 hover:bg-white"
                      )}
                      onClick={(e) => { e.stopPropagation(); favoriteMutation.mutate(creative.id); }}
                    >
                      <Heart className={cn("h-3.5 w-3.5", creative.isFavorite && "fill-current")} />
                    </Button>
                    <Button
                      data-testid={`button-download-creative-${creative.id}`}
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7 bg-white/90 hover:bg-white shadow-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (creative.imageUrl) downloadImage(creative.imageUrl, `ad-${creative.id}.png`);
                      }}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      data-testid={`button-edit-creative-${creative.id}`}
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7 bg-white/90 hover:bg-white shadow-sm"
                      onClick={(e) => { e.stopPropagation(); navigate(`/creative/${creative.id}/edit`); }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="secondary" size="icon" className="h-7 w-7 bg-white/90 hover:bg-white shadow-sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          if (creative.imageUrl) {
                            navigator.clipboard.writeText(creative.imageUrl);
                            toast({ title: "URL copied" });
                          }
                        }}>
                          <Copy className="h-4 w-4 mr-2" /> Copy URL
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMutation.mutate(creative.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {creative.isFavorite && !isSelectedForCopy && (
                    <Heart className="absolute top-2 left-10 h-4 w-4 text-rose-500 fill-rose-500 group-hover:opacity-0 transition-opacity" />
                  )}

                  <div className="absolute bottom-2 left-2 flex gap-1">
                    {creative.aspectRatio && (
                      <Badge className="bg-black/60 text-white border-0 text-[10px]">
                        {creative.aspectRatio}
                      </Badge>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {selectedForCopy.size > 0 && (
          <div className="fixed bottom-52 left-0 right-0 z-50 md:left-[260px] flex justify-center pointer-events-none">
            <div className="pointer-events-auto bg-indigo-600 text-white rounded-full shadow-xl px-5 py-2.5 flex items-center gap-3 animate-in slide-in-from-bottom-4">
              <span className="text-sm font-medium">{selectedForCopy.size} creative{selectedForCopy.size !== 1 ? "s" : ""} selected</span>
              <Button
                data-testid="button-write-ad-copy"
                size="sm"
                className="bg-white text-indigo-600 hover:bg-indigo-50 h-7 text-xs font-semibold gap-1.5 rounded-full px-3"
                onClick={() => {
                  const ids = Array.from(selectedForCopy).join(",");
                  const brandId = selectedBrandId ? Number(selectedBrandId) : (group?.brandId || "");
                  const groupName = group?.name || "Untitled";
                  navigate(`/ad-copy?addCreatives=${ids}&brandId=${brandId}&groupName=${encodeURIComponent(groupName)}`);
                }}
              >
                <MessageSquare className="h-3.5 w-3.5" />
                Write Ad Copy
              </Button>
              <button
                data-testid="button-clear-selection"
                className="h-6 w-6 rounded-full hover:bg-indigo-500 flex items-center justify-center transition-colors"
                onClick={() => setSelectedForCopy(new Set())}
              >
                <XCircle className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <div className="fixed bottom-0 left-0 right-0 z-50 md:left-[260px]">
          <div className="mx-auto max-w-5xl px-4 pb-4">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 relative">
                  <Input
                    data-testid="input-prompt"
                    placeholder="Describe your ad creative... (optional)"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && canGenerate) generateMutation.mutate();
                    }}
                    className="pr-4 h-11 rounded-xl border-gray-200 focus:border-primary/50 bg-gray-50/50"
                  />
                </div>
                <Button
                  data-testid="button-generate"
                  className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 h-11 px-6 rounded-xl gap-2"
                  onClick={() => generateMutation.mutate()}
                  disabled={!canGenerate}
                >
                  {generateMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  Generate{multiCloneUrls.length > 1 ? ` (${multiCloneUrls.length * quantity})` : ""}
                </Button>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Select value={imageModel} onValueChange={setImageModel}>
                  <SelectTrigger className="w-40 h-8 text-xs rounded-lg border-gray-200" data-testid="select-image-model">
                    <SelectValue placeholder="Image Model" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="seedream-v5-lite">Seedream V5 Lite</SelectItem>
                    <SelectItem value="nano-banana-2">NanoBanana 2</SelectItem>
                  </SelectContent>
                </Select>

                <div className="h-5 w-px bg-gray-200" />

                <Select value={selectedBrandId} onValueChange={(v) => { setSelectedBrandId(v); setSelectedProductId(""); }}>
                  <SelectTrigger className="w-36 h-8 text-xs rounded-lg border-gray-200" data-testid="select-brand">
                    <SelectValue placeholder="Brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map((b) => (
                      <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedProductId} onValueChange={setSelectedProductId} disabled={!selectedBrandId}>
                  <SelectTrigger className="w-36 h-8 text-xs rounded-lg border-gray-200" data-testid="select-product">
                    <SelectValue placeholder="Product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="h-5 w-px bg-gray-200" />

                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="button-clone-upload"
                      variant="outline"
                      size="sm"
                      className={cn(
                        "h-8 text-xs rounded-lg gap-1.5 border-gray-200",
                        (cloneImageUrl || multiCloneUrls.length > 0) && "border-primary/50 bg-primary/5 text-primary"
                      )}
                    >
                      {multiCloneUrls.length > 1 ? (
                        <div className="flex -space-x-1">
                          {multiCloneUrls.slice(0, 3).map((c, i) => (
                            <img key={i} src={c.preview} className="h-4 w-4 rounded object-cover border border-white" alt="" />
                          ))}
                        </div>
                      ) : clonePreview ? (
                        <img src={clonePreview} className="h-4 w-4 rounded object-cover" alt="" />
                      ) : (
                        <ImageIcon className="h-3.5 w-3.5" />
                      )}
                      Clone Ad{multiCloneUrls.length > 1 ? `s (${multiCloneUrls.length})` : ""}
                      {(cloneImageUrl || multiCloneUrls.length > 0) && (
                        <X
                          className="h-3 w-3 ml-1 hover:text-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCloneImageUrl("");
                            setClonePreview("");
                            setMultiCloneUrls([]);
                          }}
                        />
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className={cn("p-3", multiCloneUrls.length > 1 ? "w-80" : "w-64")} align="start">
                    <Label className="text-xs font-medium text-gray-600 mb-2 block">
                      {multiCloneUrls.length > 1 ? `${multiCloneUrls.length} templates to clone` : "Upload ad to clone"}
                    </Label>
                    {multiCloneUrls.length > 1 ? (
                      <div className="space-y-2 mb-2">
                        <div className="grid grid-cols-3 gap-2">
                          {multiCloneUrls.map((c, i) => (
                            <div key={i} className="relative rounded-lg overflow-hidden aspect-square">
                              <img src={c.preview} className="w-full h-full object-cover" alt="" />
                              <Button
                                variant="destructive"
                                size="icon"
                                className="absolute top-0.5 right-0.5 h-5 w-5"
                                onClick={() => {
                                  const updated = multiCloneUrls.filter((_, j) => j !== i);
                                  setMultiCloneUrls(updated);
                                  if (updated.length === 0) {
                                    setCloneImageUrl("");
                                    setClonePreview("");
                                  } else {
                                    setCloneImageUrl(updated[0].imageUrl);
                                    setClonePreview(updated[0].preview);
                                  }
                                }}
                              >
                                <X className="h-2.5 w-2.5" />
                              </Button>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-gray-400">
                          Each template generates {quantity} ad{quantity > 1 ? "s" : ""} = {multiCloneUrls.length * quantity} total
                        </p>
                      </div>
                    ) : clonePreview ? (
                      <div className="relative rounded-lg overflow-hidden mb-2">
                        <img src={clonePreview} className="w-full h-auto rounded-lg" alt="Clone" />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-1 right-1 h-6 w-6"
                          onClick={() => { setCloneImageUrl(""); setClonePreview(""); setMultiCloneUrls([]); }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <button
                        className="w-full h-24 border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-primary/50 hover:bg-primary/5 transition-colors"
                        onClick={() => cloneInputRef.current?.click()}
                      >
                        <Upload className="h-5 w-5 text-gray-400" />
                        <span className="text-xs text-gray-500">Click to upload</span>
                      </button>
                    )}
                    <input ref={cloneInputRef} type="file" accept="image/*" className="hidden" onChange={handleCloneUpload} />
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="button-reference-upload"
                      variant="outline"
                      size="sm"
                      className={cn(
                        "h-8 text-xs rounded-lg gap-1.5 border-gray-200",
                        referenceImageUrls.length > 0 && "border-primary/50 bg-primary/5 text-primary"
                      )}
                    >
                      <Upload className="h-3.5 w-3.5" />
                      Reference
                      {referenceImageUrls.length > 0 && (
                        <Badge className="h-4 min-w-4 px-1 text-[10px] bg-primary text-white border-0">
                          {referenceImageUrls.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-3" align="start">
                    <Label className="text-xs font-medium text-gray-600 mb-2 block">Reference images (style/mood)</Label>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      {referencePreviews.map((preview, i) => (
                        <div key={i} className="relative rounded-lg overflow-hidden aspect-square">
                          <img src={preview} className="w-full h-full object-cover" alt="" />
                          <Button
                            variant="destructive"
                            size="icon"
                            className="absolute top-0.5 right-0.5 h-5 w-5"
                            onClick={() => {
                              setReferencePreviews((prev) => prev.filter((_, j) => j !== i));
                              setReferenceImageUrls((prev) => prev.filter((_, j) => j !== i));
                            }}
                          >
                            <X className="h-2.5 w-2.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <button
                      className="w-full h-16 border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-primary/50 hover:bg-primary/5 transition-colors"
                      onClick={() => refInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4 text-gray-400" />
                      <span className="text-[11px] text-gray-500">Add reference images</span>
                    </button>
                    <input ref={refInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleReferenceUpload} />
                  </PopoverContent>
                </Popover>

                <div className="h-5 w-px bg-gray-200" />

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs rounded-lg gap-1.5 border-gray-200" data-testid="button-aspect-ratio">
                      {ASPECT_RATIOS.find((r) => r.value === aspectRatio)?.icon && (() => {
                        const Icon = ASPECT_RATIOS.find((r) => r.value === aspectRatio)!.icon;
                        return <Icon className="h-3.5 w-3.5" />;
                      })()}
                      {aspectRatio}
                      <ChevronDown className="h-3 w-3 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-40 p-2" align="start">
                    {ASPECT_RATIOS.map((ratio) => (
                      <button
                        key={ratio.value}
                        className={cn(
                          "flex items-center gap-2 w-full px-2 py-1.5 rounded text-xs hover:bg-gray-100 transition-colors",
                          aspectRatio === ratio.value && "bg-primary/10 text-primary"
                        )}
                        onClick={() => setAspectRatio(ratio.value)}
                      >
                        <ratio.icon className="h-3.5 w-3.5" />
                        {ratio.label}
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs rounded-lg gap-1.5 border-gray-200" data-testid="button-resolution">
                      <Maximize2 className="h-3.5 w-3.5" />
                      {RESOLUTIONS.find((r) => r.value === resolution)?.label || "2K"}
                      <ChevronDown className="h-3 w-3 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-28 p-2" align="start">
                    {RESOLUTIONS.map((res) => (
                      <button
                        key={res.value}
                        className={cn(
                          "flex items-center gap-2 w-full px-2 py-1.5 rounded text-xs hover:bg-gray-100 transition-colors",
                          resolution === res.value && "bg-primary/10 text-primary"
                        )}
                        onClick={() => setResolution(res.value)}
                      >
                        {res.label}
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs rounded-lg gap-1.5 border-gray-200" data-testid="button-quantity">
                      x{quantity}
                      <ChevronDown className="h-3 w-3 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-24 p-2" align="start">
                    {QUANTITIES.map((q) => (
                      <button
                        key={q}
                        className={cn(
                          "flex items-center gap-2 w-full px-2 py-1.5 rounded text-xs hover:bg-gray-100 transition-colors",
                          quantity === q && "bg-primary/10 text-primary"
                        )}
                        onClick={() => setQuantity(q)}
                      >
                        x{q}
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!previewCreative} onOpenChange={() => setPreviewCreative(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          <DialogTitle className="sr-only">Creative Preview</DialogTitle>
          {previewCreative && (
            <div>
              <img
                src={previewCreative.imageUrl || ""}
                alt={previewCreative.prompt || "Generated ad"}
                className="w-full h-auto"
              />
              <div className="p-4 space-y-2">
                {previewCreative.adCopy && (
                  <p className="text-sm font-medium text-gray-900">{previewCreative.adCopy}</p>
                )}
                {previewCreative.generatedPrompt && (
                  <details className="text-xs text-gray-500">
                    <summary className="cursor-pointer hover:text-gray-700">View generation prompt</summary>
                    <p className="mt-1 whitespace-pre-wrap">{previewCreative.generatedPrompt}</p>
                  </details>
                )}
                <div className="flex items-center gap-2 pt-2">
                  {previewCreative.aspectRatio && <Badge variant="outline" className="text-[10px]">{previewCreative.aspectRatio}</Badge>}
                  {previewCreative.resolution && <Badge variant="outline" className="text-[10px]">{previewCreative.resolution === "4096" ? "4K" : "2K"}</Badge>}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 text-[10px] gap-1 ml-auto"
                    onClick={() => {
                      if (previewCreative.imageUrl) downloadImage(previewCreative.imageUrl, `ad-${previewCreative.id}.png`);
                    }}
                  >
                    <Download className="h-3 w-3" /> Download
                  </Button>
                  <span className="text-[10px] text-gray-400">
                    {new Date(previewCreative.createdAt).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </Layout>
  );
}
